import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LabelList
} from 'recharts';
import useDailyStats from '../../hooks/useDailyStats';
import { TrendingUp } from 'lucide-react';
import { DashboardRange, DEFAULT_RANGE, rangeLabel } from '../../types/dashboardRange';
import { card, pill, sectionSub, sectionTitle, statusColors } from './dashboardTheme';

interface DailyStatsChartProps {
  range?: DashboardRange;
}

const DailyStatsChart = ({ range = DEFAULT_RANGE }: DailyStatsChartProps) => {
  const { data, isLoading } = useDailyStats(range);

  if (isLoading) {
    return (
      <div className={`${card} p-6 mb-6 animate-pulse`}>
        <div className="h-5 bg-gray-100 rounded w-1/4 mb-6"></div>
        <div className="h-64 bg-gray-50 rounded w-full"></div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return null;
  }

  // Buckets are "YYYY-MM-DD" (day) or "YYYY-MM" (month) depending on range length.
  const formatBucket = (value: string) => {
    const isMonth = value.length === 7;
    const parsed = new Date(isMonth ? `${value}-01` : value);
    return parsed.toLocaleDateString('en-IN', isMonth
      ? { month: 'short', year: '2-digit' }
      : { day: '2-digit', month: 'short' });
  };

  const chartData = data.map(item => ({
    ...item,
    formattedDate: formatBucket(item.date),
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 rounded-lg shadow-[0_4px_16px_rgba(16,24,40,0.12)]">
          <p className="text-sm font-semibold text-gray-900 mb-2">{label}</p>
          <div className="space-y-1">
            {payload.map((entry: any, index: number) => (
              <div key={index} className="flex items-center justify-between gap-4">
                <span className="text-xs font-medium" style={{ color: entry.color }}>
                  {entry.name}:
                </span>
                <span className="text-xs font-bold text-gray-900">
                  ₹{entry.value.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  const renderCustomLabel = (props: any) => {
    const { x, y, width, height, value } = props;
    if (value === 0 || height < 20) return null;

    return (
      <text
        x={x + width / 2}
        y={y + height / 2}
        fill="#1f2937"
        textAnchor="middle"
        dominantBaseline="middle"
        className="text-[10px] font-semibold"
      >
        ₹{value >= 1000 ? (value / 1000).toFixed(1) + 'k' : value}
      </text>
    );
  };

  return (
    <div className={`${card} overflow-hidden mb-6`}>
      <div className="p-4 sm:p-5 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className={sectionTitle}>Sales Overview</h2>
            <p className={`${sectionSub} mt-0.5`}>Performance for {rangeLabel(range).toLowerCase()}</p>
          </div>
          <div className={pill}>
            <TrendingUp className="w-3.5 h-3.5" />
            <span>Real-time</span>
          </div>
        </div>
      </div>
      
      <div className="p-4 sm:p-6">
        <div className="h-[350px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 10, left: -20, bottom: 0 }}
              barGap={0}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis 
                dataKey="formattedDate" 
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#64748b' }}
                dy={10}
              />
              <YAxis 
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#64748b' }}
                tickFormatter={(value) => `₹${value >= 1000 ? (value / 1000).toFixed(0) + 'k' : value}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                verticalAlign="top" 
                align="right" 
                iconType="circle"
                wrapperStyle={{ paddingBottom: '20px', fontSize: '12px' }}
              />
              <Bar
                name="Confirmed"
                dataKey="confirmed"
                stackId="a"
                fill={statusColors.confirmed}
                maxBarSize={40}
              >
                <LabelList dataKey="confirmed" content={renderCustomLabel} />
              </Bar>
              <Bar
                name="Cancelled"
                dataKey="cancel"
                stackId="a"
                fill={statusColors.cancel}
                maxBarSize={40}
              >
                <LabelList dataKey="cancel" content={renderCustomLabel} />
              </Bar>
              <Bar
                name="Pending"
                dataKey="pending"
                stackId="a"
                fill={statusColors.pending}
                radius={[4, 4, 0, 0]}
                maxBarSize={40}
              >
                <LabelList dataKey="pending" content={renderCustomLabel} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default DailyStatsChart;
